import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./index.scss";

/**
 * The overflow-menu trigger. One button, one look — consumers pick a size and
 * nothing else; the appearance lives entirely in `index.scss`.
 *
 * The dots are inline SVG painted with `currentColor` rather than an <img>. An
 * <img> can only be recoloured with a filter, which is why this used to force
 * the glyph white and why it vanished on a pale surface.
 */
const ThreeDotsButton = forwardRef(function ThreeDotsButton(
  { onClick, className = "", size = 24, ...rest },
  ref,
) {
  const { t } = useTranslation();
  return (
    <button
      ref={ref}
      className={`three-dots-button ${className}`.trim()}
      onClick={onClick}
      aria-label={t('actions.moreOptions')}
      type="button"
      // A custom property rather than width/height: an inline width beats every
      // stylesheet, which is what drove consumers to `!important` to resize it.
      style={{ '--tdb-size': `${size}px` }}
      {...rest}
    >
      <svg
        className="three-dots-icon"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="6" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="12" cy="18" r="2" />
      </svg>
    </button>
  );
});

ThreeDotsButton.propTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.number,
};

ThreeDotsButton.defaultProps = {
  onClick: null,
  className: "",
  size: 24,
};

export default ThreeDotsButton;
